import { useEffect, useState } from 'react';

import { gql, useQuery } from '@apollo/client';
import { keyBy, merge, mergeWith, values } from 'lodash';

import { useQaPreviewReportId } from './QaPreviewReportContext';
import { NULL_REPORT_ID } from '../constants/report';
import { nvlParseInt } from '../utils/afsUtils';

const query = gql`
  query CustomProjectProducts(
    $projectId: Int!
    $reportId: UUID!
    $productId: Int!
    $previewReportId: UUID!
  ) {
    allProjects(condition: { id: $projectId }) {
      nodes {
        id
        name
        customPqProductNamesByProjectId {
          edges {
            node {
              id
              productId
              productName
              marketProductLabel
              version
              productLabelColor
              isHighlighted
              reportId
            }
          }
        }
        projectReportsByProjectId {
          edges {
            node {
              reportJobByReportId {
                reportSummariesByReportId {
                  edges {
                    node {
                      productByProductId {
                        id
                        name
                      }
                      version
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    allReportJobs(condition: { reportId: $reportId }) {
      nodes {
        reportSummariesByReportId {
          edges {
            node {
              productByProductId {
                id
                name
              }
              version
            }
          }
        }
      }
    }
    previewReport: allReportJobs(condition: { reportId: $previewReportId }) {
      nodes {
        reportSummariesByReportId {
          edges {
            node {
              productByProductId {
                id
                name
              }
              version
            }
          }
        }
      }
    }
    allProducts(condition: { id: $productId }) {
      nodes {
        id
        name
      }
    }
  }
`;

interface CustomProjectProductsResponse {
  allProjects: {
    nodes: {
      id: number;
      name: string;
      customPqProductNamesByProjectId: {
        edges: CustomPqProductNamesByProjectIdRow[];
      };
      projectReportsByProjectId: {
        edges: ReportJobByReportIdRow[];
      };
    }[];
  };
  allReportJobs: {
    nodes: {
      reportSummariesByReportId: {
        edges: ReportSummariesByReportIdRow[];
      };
    }[];
  };
  previewReport?: CustomProjectProductsResponse['allReportJobs'];
  allProducts: {
    nodes: reports.ProductInfo[];
  };
}

interface CustomPqProductNamesByProjectIdRow {
  node: {
    id: number;
    productId: number;
    productName: string;
    marketProductLabel: string;
    version: string;
    productLabelColor: string;
    isHighlighted: boolean;
    reportId?: string; // Added for optimization report-specific names
  };
}

interface ReportJobByReportIdRow {
  node: {
    reportJobByReportId: {
      reportSummariesByReportId: {
        edges: ReportSummariesByReportIdRow[];
      };
    };
  };
}

interface ReportSummariesByReportIdRow {
  node: {
    productByProductId: reports.ProductInfo;
    version: string;
  };
}

const getStandardProducts = (
  data: CustomProjectProductsResponse,
  version: string,
  reportId: string,
): reports.ColorVersionedProductInfo[] => {
  const byProductId: reports.VersionedProductInfo[] =
    data.allProducts.nodes.map((n) => ({
      id: n.id,
      name: n.name,
      version: version ?? null,
      canonicalName: n.name,
    }));

  // A QA-previewed report is not in the project yet, so add its products.
  const byProjectId: reports.ColorVersionedProductInfo[] = [
    ...data.allProjects.nodes
      .flatMap((n) => n.projectReportsByProjectId.edges.map((e) => e.node))
      .map((r) => r.reportJobByReportId),
    ...(data.previewReport?.nodes ?? []),
  ]
    .flatMap((r) => r.reportSummariesByReportId.edges.map((e) => e.node))
    .map((n) => ({
      id: n.productByProductId.id,
      name: n.productByProductId.name,
      version: n.version,
      productLabelColor: null,
      canonicalName: n.productByProductId.name,
      isHighlighted: false,
    }));

  const byReportId: reports.ColorVersionedProductInfo[] =
    data.allReportJobs.nodes
      .flatMap((n) => n.reportSummariesByReportId.edges.map((e) => e.node))
      .map((n) => ({
        id: n.productByProductId.id,
        name: n.productByProductId.name,
        version: n.version,
        productLabelColor: null,
        canonicalName: n.productByProductId.name,
        isHighlighted: false,
      }));

  const standardNames: reports.ColorVersionedProductInfo[] = values(
    merge(
      keyBy(
        byProductId,
        (vpi: reports.ColorVersionedProductInfo) => vpi.id + '|' + vpi.version,
      ),
      keyBy(
        reportId === NULL_REPORT_ID ? byProjectId : byReportId,
        (vpi: reports.ColorVersionedProductInfo) => vpi.id + '|' + vpi.version,
      ),
    ),
  );

  return standardNames;
};

const getCustomProducts = (
  data: CustomProjectProductsResponse,
  _version: string,
): reports.ColorVersionedProductInfo[] => {
  const customNames: reports.ColorVersionedProductInfo[] =
    data.allProjects.nodes
      .flatMap((n) =>
        n.customPqProductNamesByProjectId.edges.map((e) => e.node),
      )
      .map((n) => ({
        id: n.productId,
        name: n.productName,
        version: n.version,
        marketProductLabel: n.marketProductLabel,
        productLabelColor: n.productLabelColor,
        customId: n.id,
        canonicalName: null,
        isHighlighted: n.isHighlighted,
        reportId: n.reportId || null, // Include reportId - null for market survey, specific UUID for optimization
      }));

  return customNames;
};

export const mergeAllProducts = (
  data: CustomProjectProductsResponse,
  version: string,
  reportId?: string,
): reports.ColorVersionedProductInfo[] => {
  const standardProducts = getStandardProducts(
    data,
    version,
    reportId ?? NULL_REPORT_ID,
  );
  const customProducts = getCustomProducts(data, version);
  const hasReportContext = Boolean(reportId && reportId !== NULL_REPORT_ID);
  const standardProductVersionKeys = new Set(
    standardProducts.map((sp) => `${sp.id}|${sp.version ?? null}`),
  );

  const normalizeVersion = (v: string | null | undefined): string | null => {
    if (v === null || v === undefined) {
      return null;
    }
    const trimmed = `${v}`.trim();
    return trimmed === '' ? null : trimmed;
  };

  const filteredCustomProducts = customProducts
    .filter((cp) =>
      standardProductVersionKeys.has(`${cp.id}|${cp.version ?? null}`),
    )
    .filter((cp) => {
      const normalizedVersion = normalizeVersion(cp.version);
      const isVersioned = normalizedVersion !== null;

      // Unversioned names are always project-scoped.
      if (!isVersioned) {
        return true;
      }

      // Legacy versioned rows with null report_id are treated as project-scoped.
      if (cp.reportId === null || cp.reportId === undefined) {
        return true;
      }

      // Versioned names are report-scoped when report_id is present.
      return hasReportContext && cp.reportId === reportId;
    })
    .map((i) => ({
      ...i,
      isHighlighted: i.isHighlighted,
    }));

  return values(
    mergeWith(
      keyBy(
        standardProducts,
        (vpi: reports.ColorVersionedProductInfo) => vpi.id + '|' + vpi.version,
      ),
      keyBy(
        filteredCustomProducts,
        (vpi: reports.ColorVersionedProductInfo) => vpi.id + '|' + vpi.version,
      ),
      (a, b) => (b === null ? a : undefined), // don't merge nulls over (particularly on canonical names)
    ),
  );
};

interface CustomNameParams {
  productId?: number;
  reportId?: string;
  projectId?: number;
  version?: string;
  refetchNames?: boolean;
  skip?: boolean;
  /** QA preview session, for callers outside a QaPreviewReportProvider. */
  previewSession?: { reportId: string } | null;
}

// default product / project number is to goto an unmatching value if not provided
export default function useCustomProductNames({
  productId = -1,
  reportId = NULL_REPORT_ID,
  projectId = -1,
  version,
  refetchNames,
  skip = false,
  previewSession,
}: CustomNameParams): reports.ColorVersionedProductInfo[] {
  const [productNames, setProductNames] = useState<
    reports.ColorVersionedProductInfo[]
  >([]);

  const contextPreviewReportId = useQaPreviewReportId();
  const previewReportId =
    previewSession?.reportId ?? contextPreviewReportId ?? NULL_REPORT_ID;

  const { data, loading, error, refetch } =
    useQuery<CustomProjectProductsResponse>(query, {
      variables: {
        projectId: nvlParseInt(projectId, -1),
        reportId,
        productId: nvlParseInt(productId, -1),
        previewReportId,
      },
      skip,
    });

  useEffect(() => {
    if (!skip) {
      refetch();
    }
  }, [refetchNames]);

  useEffect(() => {
    if (!loading && !error && data) {
      setProductNames(mergeAllProducts(data, version, reportId));
    }
  }, [data, loading, skip]);

  return productNames;
}

export function multiProjectCustomNames(
  projectIds: number[],
  skip = false,
  previewSession?: { reportId: string } | null,
): reports.ColorVersionedProductInfo[] {
  return [
    ...projectIds,
    ...Array.from({ length: 11 - projectIds.length }, () => -1),
  ]
    .map((projectId) =>
      useCustomProductNames({ projectId, skip, previewSession }),
    )
    .flat();
}
